<template>
  <div class="tree-department" v-loading="loading">
    <div class="root" @click="handleRootNodeClick">
      {{ rootDepartMent.label }}
    </div>
    <el-tree
      :data="allTreeData"
      node-key="id"
      ref="deptTree"
      :highlight-current="true"
      @node-expand="handleNodeExpand"
      @node-collapse="handleNodeCollapse"
      @node-click="handleNodeClick"
      :auto-expand-parent="true"
      :default-expanded-keys="expandedKeys"
      :expand-on-click-node="false"
      class="tree-content"
    >
      <div class="custom-tree-node" slot-scope="{ node }">
        <TextOverTooltip :content="node.label" placement="top-start" />
        <!-- 阻止冒泡 -->
        <span class="custom-tree-node-dot"
          @click.stop="() => null"
          v-if="isShowDropDownMenu(node)">
          <el-dropdown trigger="click">
            <span class="el-dropdown-link">
              <i class="el-icon-more dot"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-for="(item, index) in node.menuItems"
                :key="index"
                @click.native="item.onClick(node)"
                :style="{ color: item.color }"
                >{{ item.label }}</el-dropdown-item
              >
            </el-dropdown-menu>
          </el-dropdown>
        </span>
      </div>
    </el-tree>
    <el-button
      class="add-department"
      type="primary"
      @click="handleAddDepartment"
      v-if="judgePermission('staff_add_dept')"
    >添加部门</el-button>

    <AddDepartmentModal
      ref="addDepartmentModalRef"
      :treeData="treeData"
      @refresh="handleRefresh"
    />
  </div>
</template>
<script setup>
import {
  deleteDept,
  deptSort,
  getDeptList,
  getStaffList,
} from '@/api/domain/organize'
import { usePermission } from '@/utils/useUtils'
import { Message, MessageBox } from '@qs/ui'
import { computed, nextTick, onMounted, ref } from 'vue'
import { renderTree } from '../../../tools'
import AddDepartmentModal from './addDepartmentModal.vue'

const judgePermission = usePermission()
const deptTree = ref(null)
const loading = ref(false)
const currentNode = ref(null)
const expandedKeys = ref([])
const emits = defineEmits(['nodeChange'])
// 添加部门 ref
const addDepartmentModalRef = ref(null)

// 部门基础数据
const treeData = ref([])
const allTreeData = computed(() => {
  return treeData.value?.[0]?.children || []
})
const rootDepartMent = computed(() => {
  return treeData.value?.[0] || {}
})

const handleNodeExpand = (node) => {
  if (expandedKeys.value.includes(node?.id)) return
  expandedKeys.value.push(node?.id)
}
const removeChildNodesExpandKey = (childNodes) => {
  if (!childNodes || childNodes.length === 0) return
  childNodes.forEach((element) => {
    removeChildNodesExpandKey(element.childNodes)
    expandedKeys.value = expandedKeys.value.filter(
      (id) => id !== element.data.id,
    )
  })
}
const handleNodeCollapse = (data, { childNodes = [] }) => {
  // 收回当前权限 还需要收回所有子的权限
  childNodes && removeChildNodesExpandKey(childNodes)
  expandedKeys.value = expandedKeys.value.filter((id) => id !== data.id)
}

// 获取部门节点数据
const getDeptListData = async () => {
  loading.value = true
  const { code, data } = await getDeptList({
    allVisible: true,
  })
  loading.value = false
  if (code === 1) {
    treeData.value = renderTree(data)
  }
}

// 新增添加部门 需要定位到新增的部门 节点
const handleAddDepartment = () => addDepartmentModalRef.value?.open(null)

// 添加子部门 需要定位到新增的部门 节点
const handleAddSubDepartment = (node) =>
  addDepartmentModalRef.value?.open(node, true)

// 编辑部门 需要定位到编辑的部门
const handleEidt = (node) => addDepartmentModalRef.value.open(node)

// 删除部门 需要定位到跟部门 或者删除的上一级父部门
const handleDelete = async ({ data, childNodes }) => {
  const { id, label } = data
  const res = await getStaffList({ deptId: id, pageSize: 9999, pageNum: 1 })
  if (res?.data?.totalNum > 0) {
    MessageBox.confirm(
      '该部门下仍有员工，无法删除，请将员工账号迁移到其他部门后再重试。',
      '删除部门',
      {
        showCancelButton: false,
        confirmButtonText: '知道了',
        type: 'error',
      },
    )
    return
  }

  if (childNodes && childNodes.length > 0) {
    MessageBox.confirm(
      '请删除此部门下的成员或子部门后，再删除此部门。',
      '删除部门',
      {
        showCancelButton: false,
        confirmButtonText: '返回',
        type: 'error',
      },
    )
    return
  }

  MessageBox.confirm(`是否删除【${label}】部门？`, '删除部门', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    customClass: 'qs-message-box-break-all',
  }).then(async () => {
    // 删除部门
    const { code } = await deleteDept({ id })
    if (code === 1) {
      Message.success('部门已删除')
      handleNodeCollapse(data, { childNodes: [] })
      handleRefresh(data.parent)
    }
  }).catch(() => null)
}
// 刷新部门数据 并定位到当前部门 node
const handleRefresh = async (node) => {
  if (!node) return
  await getDeptListData()
  nextTick(() => {
    handleNodeExpand(node)
    deptTree.value?.setCurrentKey(node.id)
    handleNodeClick(node)
  })
}

// 排序 需要定位移动部门 右侧人员随之变化
const handleSort = async (type, { data }) => {
  const { prev, next, id } = data
  // 排序 up 找上一个node； down 找下一个node
  const anotherId = type === 'up' ? prev : next
  if (!anotherId) {
    return Message.warnning('当前位置无法移动')
  }
  const { code } = await deptSort({ oneId: id, anotherId })
  if (code === 1) {
    Message.success('部门移动成功')
    // 刷新数据
    handleRefresh(data)
  }
}

// 公共按钮菜单数据
const dropdownMenuItems = (node) => {
  const { prev, next, pid, level } = node.data

  const menu = []
  if (level !== 6 && judgePermission('staff_add_sub_dept')) {
    menu.unshift({
      label: '添加子部门',
      onClick: () => handleAddSubDepartment(node),
      color: 'rgba(0, 0, 0, 0.9)',
    })
  }
  if (pid === 0) {
    return menu
  }
  judgePermission('staff_edit_dept') &&
    menu.push({
      label: '编辑',
      onClick: handleEidt,
      color: 'rgba(0, 0, 0, 0.9)',
    })

  if (prev && judgePermission('staff_sort_dept')) {
    menu.push({
      label: '上移',
      onClick: () => handleSort('up', node),
      color: 'rgba(0, 0, 0, 0.9)',
    })
  }
  if (next && judgePermission('staff_sort_dept')) {
    menu.push({
      label: '下移',
      onClick: () => handleSort('down', node),
      color: 'rgba(0, 0, 0, 0.9)',
    })
  }
  judgePermission('staff_delete_dept') &&
    menu.push({
      label: '删除',
      onClick: handleDelete,
      color: '#E34D59',
    })

  return menu
}

const isShowDropDownMenu = (node) => {
  const menuItems = dropdownMenuItems(node)
  // 这里是为了给每个node添加 能够展示的按钮操作 即dropdown的menuItems
  // eslint-disable-next-line no-param-reassign
  node.menuItems = menuItems
  return menuItems.length > 0
}

const handleRootNodeClick = () => {
  deptTree.value?.setCurrentKey(null)
  emits('nodeChange', rootDepartMent.value)
}

const handleNodeClick = (node = null) => {
  if (!node) return
  currentNode.value = node
  emits('nodeChange', node)
}

onMounted(async () => {
  // 初始化获取部门数据
  await getDeptListData()
  handleNodeClick(treeData.value?.[0])
})

const resetCurrentKey = (id) => {
  handleNodeExpand({ id })
  deptTree.value?.setCurrentKey(id)
}

defineExpose({
  treeData,
  resetCurrentKey,
})
</script>

<style scoped lang="scss">
.root {
  height: 40px;
  line-height: 40px;
  border-width: 0px 0px 1px 0px;
  border-style: solid;
  border-color: #eeeef0;
  font-family: PingFang SC;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0em;
  /* 信息/font-black1 */
  /* 样式描述：强调信息 */
  color: rgba(0, 0, 0, 0.9);
  margin: 5px 0;
  cursor: pointer;
}
.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
  overflow: hidden;
  > .custom-tree-node-label {
    flex: 1;
    width: auto;
    padding-right: 8px;
    overflow: hidden; /* 确保超出容器的内容被裁剪 */
    white-space: nowrap; /* 确保文本在一行内显示 */
    text-overflow: ellipsis; /* 超出部分显示省略号 */
  }
  > .custom-tree-node-dot {
    display: inline-block;
  }
}
.tree-department {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  // transform: scaleX(1);
}
.tree-content {
  flex: 1;
  // height: 100%;
  overflow-y: auto;
  margin-bottom: 50px;
}
.add-department {
  position: sticky;
  text-align: center;
  bottom: 5px;
  width: 198px;
}
.dot {
  font-size: 12px;
  opacity: 0.8;
}
::v-deep(.el-tree) {
  width: 100%;
}
</style>
